<template>
    <div v-if="headings.length >= 1" class="directory p-4">
        <p class="text-xl font-bold mb-6 text-gray-800 flex items-center justify-between">
            {{ $t('table_of_contents') }}
            <i :class="['block lg:hidden cursor-pointer relative fi top-1 text-blue', mobileToggle ? 'fi-rr-plus' : 'fi-rr-cross']" @click="mobileToggle = !mobileToggle"></i>
        </p>

        <ul v-if="!mobileToggle" class="space-y-4">
            <li
                v-for="(heading, index) in headings"
                :key="index"
                :class="['group bg-white rounded-lg p-6 transition', isHeadingActive(heading) ? 'shadow-[0_0_10px_rgba(59,130,246,0.4)]' : 'shadow-md', 'hover:border-blue']"
            >
                <span
                    :class="[
                        getHeadingClasses(heading.tag),
                        'flex items-center cursor-pointer transition-all duration-200 font-semibold group-hover:text-blue',
                        isHeadingActive(heading) ? 'text-blue' : ''
                    ]"
                    @click.stop="scrollToHeading(heading.id)"
                >
                    <span v-if="headings.length > 1" class="flex-shrink-0 text-2xl font-bold text-blue mr-3"> {{ index + 1 }}. </span>
                    <span class="flex-1 leading-snug">
                        {{ heading.text }}
                    </span>
                </span>

                <ul v-if="heading.children?.length" class="ml-4 mt-2 space-y-4 border-l border-gray-200">
                    <li
                        v-for="(subheading, subIndex) in heading.children"
                        :key="subIndex"
                        :class="[
                            'text-sm cursor-pointer transition transform duration-2000 border-l-2 pl-4',
                            isHeadingActive(subheading) ? 'font-semibold border-blue' : 'opacity-80 hover:opacity-100  hover:font-semibold'
                        ]"
                        @click.stop="scrollToHeading(subheading.id)"
                    >
                        {{ subheading.text }}
                    </li>
                </ul>
            </li>
        </ul>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';

const props = defineProps({
    containerClass: {
        type: String,
        default: 'description-container'
    }
});

const headings = ref([]);
const activeHeadingId = ref(null);
let mutationObserver = null;
const mobileToggle = ref(false);

const generateTableOfContents = () => {
    const contentContainer = document.querySelector(`.${props.containerClass}`);

    if (!contentContainer) {
        headings.value = [];

        return;
    }

    const headingElements = Array.from(contentContainer.querySelectorAll('h1, h2, h3'));
    const headingObjects = headingElements.map((headingElement) => {
        if (!headingElement.id) {
            headingElement.id = headingElement.textContent
                .toLowerCase()
                .replace(/[^ \w\s]/g, '')
                .replace(/\s+/g, '-');
        }

        return {
            id: headingElement.id,
            text: headingElement.textContent.trim(),
            tag: headingElement.tagName.toLowerCase(),
            children: []
        };
    });

    const headingStructure = [];
    let lastLevelOneHeading = null;
    let lastLevelTwoHeading = null;

    headingObjects.forEach((headingObj) => {
        if (headingObj.tag === 'h1') {
            headingStructure.push(headingObj);
            lastLevelOneHeading = headingObj;
            lastLevelTwoHeading = null;
        } else if (headingObj.tag === 'h2') {
            if (lastLevelOneHeading) {
                lastLevelOneHeading.children.push(headingObj);
            } else {
                headingStructure.push(headingObj);
            }

            lastLevelTwoHeading = headingObj;
        } else if (headingObj.tag === 'h3') {
            if (lastLevelTwoHeading) {
                lastLevelTwoHeading.children.push(headingObj);
            } else if (lastLevelOneHeading) {
                lastLevelOneHeading.children.push(headingObj);
            } else {
                headingStructure.push(headingObj);
            }
        }
    });

    headings.value = headingStructure;
};

const scrollToHeading = (headingId) => {
    const targetElement = document.getElementById(headingId);

    if (targetElement) {
        targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    }
};

const hasDescendantHeading = (headingObj, headingId) => headingObj.children?.some((child) => child.id === headingId || hasDescendantHeading(child, headingId)) ?? false;

const isHeadingActive = (headingObj) => headingObj.id === activeHeadingId.value || hasDescendantHeading(headingObj, activeHeadingId.value);

const getHeaderOffset = () => {
    const header = document.querySelector('header');

    return header ? header.offsetHeight : 0;
};

const handleScroll = () => {
    const offset = getHeaderOffset();
    const allHeadings = Array.from(document.querySelectorAll('h1, h2, h3'));

    if (!allHeadings.length) return;

    let currentHeadingId = allHeadings[0].id;
    for (const headingElement of allHeadings) {
        if (headingElement.getBoundingClientRect().top <= offset) {
            currentHeadingId = headingElement.id;
        } else {
            break;
        }
    }

    activeHeadingId.value = currentHeadingId;
};

const getHeadingClasses = (tag) => ({ h1: 'text-base font-semibold', h2: 'text-sm font-medium pl-2', h3: 'text-sm pl-4' })[tag] || '';

const headingExists = (headings, targetId) => {
    return headings.some((heading) => {
        return heading.id === targetId || (heading.children && headingExists(heading.children, targetId));
    });
};

const initializeActiveHeading = () => {
    if (headings.value.length) {
        activeHeadingId.value = headings.value[0].id;
    }
};

const setupScrollHandler = () => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
};

const setupMutationObserver = () => {
    mutationObserver = new MutationObserver(() => {
        generateTableOfContents();
        handleScroll();
    });

    const contentContainer = document.querySelector(`.${props.containerClass}`);

    if (contentContainer) {
        mutationObserver.observe(contentContainer, {
            childList: true,
            subtree: true
        });
    }
};

watch(headings, () => {
    if (!headingExists(headings.value, activeHeadingId.value)) {
        activeHeadingId.value = headings.value[0]?.id || null;
    }
});

onMounted(() => {
    nextTick(() => {
        generateTableOfContents();
        initializeActiveHeading();
        setupScrollHandler();
        setupMutationObserver();
    });
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll);

    mutationObserver?.disconnect();
});
</script>
<style>
.description-container h1,
.description-container h2,
.description-container h3 {
    scroll-margin-top: 80px;
}
</style>
